"use client";

import Image from "next/image";
import { motion } from "framer-motion";

const steps = [
  {
    num: "01",
    title: "Upload any tax documents",
    description:
      "Drop your client's entire document package. K-1s, 1099s, brokerage statements — we take everything. Encrypted upload, no format restrictions.",
    visual: (
      <div className="w-full h-40 bg-landing-bg rounded-xl flex items-center justify-center border border-landing-text/5">
        <div className="flex flex-wrap gap-2 p-4 justify-center">
          <span className="px-3 py-1 bg-white shadow-sm rounded-sm font-mono text-[10px]">W-2.PDF</span>
          <span className="px-3 py-1 bg-white shadow-sm rounded-sm font-mono text-[10px]">1099-B.JPG</span>
          <span className="px-3 py-1 bg-white shadow-sm rounded-sm font-mono text-[10px]">K1_ENTITY.PDF</span>
        </div>
      </div>
    ),
  },
  {
    num: "02",
    title: "AI agents process everything",
    description:
      "Our agents don't sleep. They read every document, reconcile the data according to IRS tax code, and perform compliance checks across all form types, no matter how complex.",
    visual: (
      <div className="w-full h-40 bg-landing-text rounded-xl flex flex-col items-center justify-center overflow-hidden">
        <div className="w-3/4 h-1 bg-landing-primary/20 rounded-full overflow-hidden">
          <div className="h-full bg-landing-primary w-2/3" />
        </div>
        <div className="mt-4 font-mono text-[10px] text-white opacity-50 uppercase tracking-widest">
          Extracting K-1 basis...
        </div>
      </div>
    ),
  },
  {
    num: "03",
    title: "Populates your software",
    description:
      "We integrate seamlessly with your tax software — UltraTax, CCH Axcess, Prosystem, Drake, and more. Data flows straight from our agents into your return.",
    visual: (
      <div className="w-full bg-landing-bg rounded-xl flex flex-col items-center justify-center border border-landing-text/5 p-4 gap-3">
        {["UltraTax", "CCH Axcess", "Prosystem"].map((name) => (
          <div key={name} className="flex items-center justify-between w-full px-6 py-2 bg-white rounded shadow-sm border border-gray-100">
            <span className="font-bold text-xs">{name}</span>
            <span className="material-symbols-outlined text-landing-success text-sm">check_circle</span>
          </div>
        ))}
        <span className="text-xs font-medium text-landing-text-secondary">+ more</span>
      </div>
    ),
  },
];

export function ProductWalkthrough() {
  return (
    <section id="how-it-works" className="py-32 bg-landing-surface/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 relative">
          {/* Connector Line */}
          <div className="hidden lg:block absolute top-6 left-[15%] right-[15%] h-[2px] bg-landing-primary/40" />

          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="relative z-10 flex flex-col items-start"
            >
              <div className="w-12 h-12 rounded-full bg-landing-primary text-white flex items-center justify-center font-mono font-bold mb-6">
                {step.num}
              </div>
              <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
              <p className="text-landing-text-secondary leading-relaxed mb-6">{step.description}</p>
              {step.visual}
            </motion.div>
          ))}
        </div>

        {/* GIF Demo Area */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-24 rounded-3xl overflow-hidden"
        >
          <div className="bg-landing-text rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 relative overflow-hidden">
            {/* Amethyst gradient background */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-landing-primary via-transparent to-transparent" />
            </div>

            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              {/* Title */}
              <div className="lg:w-1/3 text-center lg:text-left">
                <h4 className="text-white text-2xl sm:text-3xl md:text-4xl font-serif font-bold leading-tight">
                  Watch complex forms populate into your tax software like magic.
                </h4>
              </div>

              {/* GIF */}
              <div className="lg:w-2/3 rounded-xl overflow-hidden shadow-2xl border border-white/10">
                <Image
                  src="/data-entry.gif"
                  alt="Watch Dezu populate a tax return in real-time"
                  width={1200}
                  height={675}
                  className="w-full h-auto"
                  unoptimized
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
